/** @module store/api */

/**
 * Defines IDs for API data loading calls.
 * For each ID, the `isLoading`, `loaded`, `count` state is tracked.
 * Defined to avoid ID typos.
 *
 * @constant
 *
 */
export const API_LOADING_IDS = ['CURRENT_USER_PERMISSION_EMS', 'CURRENT_USER_PERMISSION_GROUP', 'USERS']

/**
 * API store state.
 *
 * - `loading` is a global state, which tracks if any API call is performing. Having `loading = true` will show a spinning cursor.
 * - `isLoading` allows to tack, if a particular API call is performing.
 * - `loaded` allows to track, if a particular API call was performing.
 * - 'count' tracks, the number of calls of a particular API call.
 *
 */
const state = {
  loading: false,
  isLoading: {},
  loaded: {},
  count: {}
}

const getters = {
  isLoading: (state) => {
    return (id) => {
      return state.isLoading[id]
    }
  },
  loaded: (state) => {
    return (id) => {
      return state.loaded[id]
    }
  },
  numCalls: (state) => {
    return (id) => {
      return state.count[id] || 0
    }
  }
}

const mutations = {
  SET_LOADING(state, { id, status = true }) {
    if (!API_LOADING_IDS.includes(id)) {
      throw new Error(`The API ID ${id} is NOT supported.`)
    }

    if (status) {
      state.loading = true
    }

    state.isLoading[id] = status

    if (status) {
      if (state.count[id] === undefined) {
        state.count[id] = 1
      } else {
        state.count[id]++
      }
    }
  },
  SET_LOADED(state, { id, status = true }) {
    if (!API_LOADING_IDS.includes(id)) {
      throw new Error(`The API ID ${id} is NOT supported.`)
    }

    state.isLoading[id] = false

    let finished = true
    for (const id in state.isLoading) {
      finished = finished && state.isLoading[id]
    }
    state.loading = finished

    state.loaded[id] = status
  },
  STARTED(state) {
    state.loading = true
  },
  STOPPED(state) {
    state.loading = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations
}
